/**
 * This file is loaded by
 *  * the webpack build system to associate paths with components for early hints
 *  * the react router - to find the components to run for a particular path.
 *
 * Do your absolute best not to import many modules -
 * marty barreling could occur in the build system, causing issues.
 */

export interface RouteConfigJson {
  path: string;
  component?: string;
  routeType: 'ROUTE' | 'ACCOUNT_ROUTE' | 'REDIRECT' | 'PDP_ROUTE';
  exact?: boolean;
  to?: string;
  suppressScrollOnRouteChange?: string;
}

/**
 * The return from this function is used in the webpack early hints processor
 * and by the react router
 */
export function getRouteConfig(egcUrl: string, vipDashboardUrl: string, handlesReturnShipping: boolean): RouteConfigJson[] {
  /*
   * Order of routes is important! When creating routes, the most specific route needs to occur before its counterparts.
   *  this is because React Router does partial matching
   * - Example:
   *     '/c/:pageName',
   *     '/c/'
   */
  const routesConfig: RouteConfigJson[] = [
    {
      path: '/',
      component: 'LazyLanding',
      routeType: 'ROUTE',
      exact: true
    },
    /* Search */
    {
      path: '/filters/search*',
      component: 'LazySearch',
      routeType: 'ROUTE',
      suppressScrollOnRouteChange: 'shouldSearchIgnoreScrollToTop'
    },
    {
      path: '/search*',
      component: 'LazySearch',
      routeType: 'ROUTE',
      suppressScrollOnRouteChange: 'shouldSearchIgnoreScrollToTop'
    },
    {
      path: '/filters/*/*.zso',
      component: 'LazySearch',
      routeType: 'ROUTE',
      suppressScrollOnRouteChange: 'shouldSearchIgnoreScrollToTop'
    },
    {
      path: '/filters/*.zso',
      component: 'LazyRouteNotFound',
      routeType: 'ROUTE'
    },
    {
      path: '/*/*.zso',
      component: 'LazySearch',
      routeType: 'ROUTE',
      suppressScrollOnRouteChange: 'shouldSearchIgnoreScrollToTop'
    },
    {
      path: '/cart',
      component: 'LazyCart',
      routeType: 'ROUTE'
    },
    {
      path: '/checkout',
      component: 'LazyCheckout',
      routeType: 'ROUTE',
      suppressScrollOnRouteChange: 'shouldCheckoutIgnoreScrollToTop'
    },
    /* Order Confirmation */
    {
      path: '/confirmation/:purchaseId',
      component: 'LazyOrderConfirmation',
      routeType: 'ROUTE'
    },
    {
      path: '/p/asin/:asin',
      component: 'LazyAsinStockIdProductDetail',
      routeType: 'PDP_ROUTE',
      suppressScrollOnRouteChange: 'alwaysIgnoreScrollToTop'
    },
    {
      path: '/product/stockId/:stockId',
      component: 'LazyAsinStockIdProductDetail',
      routeType: 'PDP_ROUTE',
      suppressScrollOnRouteChange: 'alwaysIgnoreScrollToTop'
    },
    {
      path: '/p/:seoName/product/:productId/color/:colorId',
      component: 'LazyProductDetail',
      routeType: 'PDP_ROUTE',
      suppressScrollOnRouteChange: 'alwaysIgnoreScrollToTop'
    },
    {
      path: '/p/:seoName/product/:productId',
      component: 'LazyProductDetail',
      routeType: 'PDP_ROUTE',
      suppressScrollOnRouteChange: 'alwaysIgnoreScrollToTop'
    },
    {
      path: '/p/product/:productId/color/:colorId',
      component: 'LazyProductDetail',
      routeType: 'PDP_ROUTE',
      suppressScrollOnRouteChange: 'alwaysIgnoreScrollToTop'
    },
    {
      path: '/p/product/:productId',
      component: 'LazyProductDetail',
      routeType: 'PDP_ROUTE',
      suppressScrollOnRouteChange: 'alwaysIgnoreScrollToTop'
    },
    {
      path: '/product/review/add/media/:productId/color/:colorId',
      component: 'LazyAddReview',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/add/media/:productId',
      component: 'LazyAddReview',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/p/add/:productId/color/:colorId',
      component: 'LazyAddReview',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/p/add/:productId',
      component: 'LazyAddReview',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/add/:productId/color/:colorId',
      component: 'LazyAddReview',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/add/:productId',
      component: 'LazyAddReview',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/:productId/page/:reviewsPage/start/:reviewsStart/orderBy/:orderBy/review/:reviewId',
      component: 'LazyProductReviews',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/:productId/page/:reviewsPage/start/:reviewsStart/orderBy/:orderBy',
      component: 'LazyProductReviews',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/:productId/page/:reviewsPage/start/:reviewsStart',
      component: 'LazyProductReviews',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/:productId/page/:reviewsPage/orderBy/:orderBy',
      component: 'LazyProductReviews',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/:productId/page/:reviewsPage',
      component: 'LazyProductReviews',
      routeType: 'ROUTE'
    },
    {
      path: '/product/review/:productId',
      component: 'LazyProductReviews',
      routeType: 'ROUTE'
    },
    {
      path: '/product/:productId/color/:colorId',
      component: 'LazyProductDetail',
      routeType: 'PDP_ROUTE',
      suppressScrollOnRouteChange: 'alwaysIgnoreScrollToTop'
    },
    {
      path: '/product/:productId',
      component: 'LazyProductDetail',
      routeType: 'PDP_ROUTE',
      suppressScrollOnRouteChange: 'alwaysIgnoreScrollToTop'
    },
    {
      path: '/createReview/asin/:asin',
      component: 'LazyAsinAddReview',
      routeType: 'ROUTE'
    },
    /* My Account Start */
    {
      path: '/account/favorites/:id',
      component: 'LazyCollection',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/account/favorites',
      component: 'LazyCollections',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/account/privacy-data-sharing',
      component: 'LazyPrivacyDataSharing',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/account',
      component: 'LazyAccount',
      routeType: 'ACCOUNT_ROUTE',
      suppressScrollOnRouteChange: 'shouldAccountIgnoreScrollToTop'
    },
    {
      path: '/favorites/:id',
      component: 'LazyCollection',
      routeType: 'ROUTE'
    },
    {
      path: '/collection/:id',
      component: 'LazyCollection',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/login',
      component: 'LazyFederatedLogin',
      routeType: 'ROUTE'
    },
    {
      path: '/federated-login',
      component: 'LazyFederatedLogin',
      routeType: 'ROUTE'
    },
    {
      path: '/rewards/optin/emailId/:emailId',
      component: 'LazyVipOptin',
      routeType: 'ROUTE'
    },
    {
      path: '/rewards/confirmed',
      component: 'LazyVipOptinAck',
      routeType: 'ROUTE'
    },
    {
      path: '/vip/optin/emailId/:emailId',
      component: 'LazyVipOptin',
      routeType: 'ROUTE'
    },
    {
      path: '/vip/confirmed',
      component: 'LazyVipOptinAck',
      routeType: 'ROUTE'
    },
    {
      path: '/vip/alreadyEnrolled',
      component: 'LazyVipOptinAck',
      routeType: 'ROUTE'
    },
    {
      path: '/orders/:orderId/cancel',
      component: 'LazyCancelOrder',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/my-orders/:orderId/cancel',
      component: 'LazyCancelMyOrder',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/orders/:orderId',
      component: 'LazyOrder',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/orders',
      component: 'LazyOrderHistory',
      routeType: 'ACCOUNT_ROUTE',
      suppressScrollOnRouteChange: 'shouldAccountIgnoreScrollToTop'
    },
    {
      path: '/my-orders/:orderId',
      component: 'LazyMyOrderDetails',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/item-details/:parentOrderId/:itemUniqueId/',
      component: 'LazyItemDetails',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/my-orders',
      component: 'LazyMyOrders',
      routeType: 'ACCOUNT_ROUTE',
      suppressScrollOnRouteChange: 'shouldAccountIgnoreScrollToTop'
    },
    {
      path: '/return-item',
      component: 'LazyReturnFlowReturnItem',
      routeType: 'ACCOUNT_ROUTE',
      suppressScrollOnRouteChange: 'shouldAccountIgnoreScrollToTop'
    },
    {
      path: '/shipments/:orderId/:shipmentId',
      component: 'LazyShipmentTracking',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/payments/:paymentId/edit',
      component: 'LazyEditPayment',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/payments/new',
      component: 'LazyAddPayment',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/payments',
      component: 'LazyPayments',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/addresses/:addressId/edit',
      component: 'LazyEditShipping',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/addresses/new',
      component: 'LazyAddShipping',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/addresses',
      component: 'LazyShipping',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/returns',
      component: 'LazyReturnAll',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/my-returns',
      component: 'LazyMyReturns',
      routeType: 'ACCOUNT_ROUTE',
      suppressScrollOnRouteChange: 'shouldAccountIgnoreScrollToTop'
    },
    {
      path: '/return',
      exact: true,
      component: 'LazyReturns',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/return/cancel',
      exact: true,
      component: 'LazyCancelReturn',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/my-return/cancel',
      exact: true,
      component: 'LazyCancelMyReturn',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/returnInitiate',
      component: 'LazyReturns',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/returnLabel',
      component: 'LazyReturnLabel',
      routeType: 'ROUTE'
    },
    {
      path: '/return-info/:contractId/:shipmentLineItemId',
      component: 'LazyReturnInfo',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/subscriptions/:tokenTypeOrAction/:token',
      component: 'LazySubscriptions',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/subscriptions/:tokenTypeOrAction',
      component: 'LazySubscriptions',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/subscriptions/:token',
      component: 'LazySubscriptions',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/subscriptions',
      component: 'LazySubscriptions',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/create-label',
      component: 'LazyCreateLabel',
      routeType: 'ROUTE'
    },
    {
      path: '/exchange',
      component: 'LazyExchange',
      routeType: 'ACCOUNT_ROUTE'
    },
    {
      path: '/c/survey',
      component: 'LazyHmdSurvey',
      routeType: 'ROUTE'
    },
    {
      path: '/c/account-deletion-request',
      component: 'LazyDeleteAccount',
      routeType: 'ROUTE'
    },
    {
      path: '/survey',
      component: 'LazyHmdSurvey',
      routeType: 'ROUTE'
    },
    {
      path: '/c/:pageName',
      component: 'LazyLanding',
      routeType: 'ROUTE'
    },
    {
      path: '/c/*',
      component: 'LazyRouteNotFound',
      routeType: 'ROUTE'
    },
    {
      path: '/b/:brandName/brand/:brandId',
      component: 'LazyTaxonomyBrand',
      routeType: 'ROUTE'
    },
    {
      path: '/:brandName/brand/:brandId',
      component: 'LazyTaxonomyBrand',
      routeType: 'ROUTE'
    },
    {
      path: '/brand/:brandId',
      component: 'LazyTaxonomyBrand',
      routeType: 'ROUTE'
    },
    {
      path: '/egiftcard',
      to: egcUrl,
      routeType: 'REDIRECT'
    },
    {
      path: '/gift-certificate',
      to: egcUrl,
      routeType: 'REDIRECT'
    },
    {
      path: '/product',
      component: 'LazyRouteNotFound',
      routeType: 'ROUTE'
    },
    {
      path: '/p',
      component: 'LazyRouteNotFound',
      routeType: 'ROUTE'
    },
    {
      path: '/a/the-style-room/p/:seoName/product/:productId/color/:colorId',
      component: 'LazyProductDetail',
      suppressScrollOnRouteChange: 'alwaysIgnoreScrollToTop',
      routeType: 'PDP_ROUTE'
    },
    {
      path: '/a/the-style-room/p/:seoName/product/:productId',
      component: 'LazyProductDetail',
      suppressScrollOnRouteChange: 'alwaysIgnoreScrollToTop',
      routeType: 'PDP_ROUTE'
    },
    {
      path: '/error/ccBlocked',
      component: 'LazyCostlyCustomerPage',
      routeType: 'ROUTE'
    },
    {
      path: '/error/:statusToSend',
      component: 'LazyRouteNotFound',
      routeType: 'ROUTE'
    },
    {
      path: '/s/e/c/r/e/t/r/o/u/t/e',
      component: 'LazyBrokenPage',
      routeType: 'ROUTE'
    }
  ];
  if (vipDashboardUrl) {
    routesConfig.push(
      {
        path: '/vip/dashboard',
        to: vipDashboardUrl,
        routeType: 'REDIRECT'
      },
      {
        path: '/rewards/dashboard',
        to: vipDashboardUrl,
        routeType: 'REDIRECT'
      }
    );
  }

  routesConfig.push(
    /* Prevent these requests from hitting the wildcard container. These should be 404ing since they are not valid seo term urls */
    {
      path: '/*_*',
      component: 'LazyRouteNotFound',
      routeType: 'ROUTE'
    },
    {
      path: '/*.*',
      component: 'LazyRouteNotFound',
      routeType: 'ROUTE'
    },
    {
      path: '/:seoName/',
      component: 'LazyWildCard',
      routeType: 'ROUTE',
      exact: true,
      suppressScrollOnRouteChange: 'shouldSearchIgnoreScrollToTop'
    },
    /* If none of our known routes match, show a nice 404 page.
             This must be the last route under the App component or else it'll match valid routes.
        */
    {
      path: '*',
      component: 'LazyRouteNotFound',
      routeType: 'ROUTE'
    },
    /* No APP Routes */
    {
      path: '/i/:productId/color/:colorId/style/:styleId/:index',
      to: '/p/product/:productId/color/:colorId',
      routeType: 'REDIRECT'
    },
    {
      path: '/i/:productId/style/:styleId/:index',
      to: '/p/product/:productId',
      routeType: 'REDIRECT'
    },
    {
      path: '/c/symphonypreview',
      component: 'LazySymphonyPreview',
      routeType: 'ROUTE'
    },
    {
      path: '/contentPreview',
      component: 'LazySymphonyPreview',
      routeType: 'ROUTE'
    }
  );

  if (!handlesReturnShipping) {
    routesConfig.push({
      path: '/uspsLabelDisplay',
      component: 'LazyReturnLabel',
      routeType: 'ROUTE'
    });
  }
  return routesConfig;
}
